import { createPortal } from 'react-dom';

import { useEffect, useState } from 'react';

import type { ObjectDragEvent, ObjectDragOver } from '@fabric-app/business/types/draggable';
import { useDragAndSelect } from '@fabric-app/business/lib/DragAndSelectContext';
import { FloatingFooter } from '@fabric-app/ui/components/FloatingFooter';
import styled from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { motion } from 'framer-motion';
import { shallow } from 'zustand/shallow';
import useUIStore from '@/src/store/ui';
import { pick } from '@fabric-app/utils/object/manipulation';

const FOOTER = {
  key: 'drop-to-move',
  priority: 150,
};

const DropToMove: React.FC<{
  onDropAction: (fdocIds: string[]) => void;
}> = ({ onDropAction }) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const dragAndSelectContext = useDragAndSelect();
  const { setFloatingFooter, floatingFooterActive } = useUIStore(
    (state) => pick(state, ['floatingFooterActive', 'floatingFooters', 'setFloatingFooter']),
    shallow,
  );

  const isDragging = dragAndSelectContext?.isDragging;

  useEffect(() => {
    setFloatingFooter(FOOTER, !!isDragging);
  }, [isDragging, setFloatingFooter]);

  const [draggingItemsOver, setDraggingItemsOver] = useState<number>(0);
  useEffect(() => {
    if (!ref) return;

    const onDragOver = (e: CustomEvent<ObjectDragOver>) => {
      const itemIds =
        e.detail.objectIds && (e.detail.objectIds.length ?? 0) > 0
          ? e.detail.objectIds
          : [e.detail.objectId];

      setDraggingItemsOver(itemIds.length);
    };

    const onDragOut = () => {
      setDraggingItemsOver(0);
    };

    const onDragDrop = (e: CustomEvent<ObjectDragEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setDraggingItemsOver(0);

      const itemIds =
        e.detail.objectIds && (e.detail.objectIds.length ?? 0) > 0
          ? e.detail.objectIds
          : [e.detail.objectId];

      onDropAction(itemIds);
    };

    ref.addEventListener('object-drag-over', onDragOver);
    ref.addEventListener('object-drag-out', onDragOut);
    ref.addEventListener('object-drag-drop', onDragDrop);

    return () => {
      ref?.removeEventListener('object-drag-over', onDragOver);
      ref?.removeEventListener('object-drag-out', onDragOut);
      ref?.removeEventListener('object-drag-drop', onDragDrop);
    };
  }, [onDropAction, ref]);

  if (!dragAndSelectContext?.isDragging || floatingFooterActive !== FOOTER.key) return null;

  return createPortal(
    <FloatingFooter.Container
      setRef={setRef}
      data-droppable
      data-drop-over={draggingItemsOver > 0}
      highlighted={draggingItemsOver > 0}
    >
      <Text as={motion.div} layout>
        {'Drop here to move items'}
      </Text>
    </FloatingFooter.Container>,
    document.body,
  );
};

export default DropToMove;

const Text = styled.div`
  font-size: 0.875rem;
  height: 40px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;
  font-weight: 500;
  color: ${cssVar['color-text-primary']};
  border: 1px dashed rgba(${cssVar['color-text-primary-rgb']}, 0.3);
  border-radius: 10px;
`;
